import { useEffect, useRef, useState, useCallback } from "react";

const DEFAULT_AUDIO_PATH = "/alert.wav";

/**
 * Hook to preload and play the alert notification sound (`public/alert.wav`).
 * Handles rapid re-triggers, volume control, browser autoplay restrictions, and component cleanup.
 *
 * @param {Object} [options={}]
 * @param {number} [options.volume=1.0] - Audio volume (0.0 to 1.0)
 * @param {string} [options.src] - Audio file URL (defaults to /alert.wav)
 * @param {boolean} [options.loop=false] - Whether to loop the sound
 * @returns {{ play: () => Promise<void>, stop: () => void, isPlaying: boolean }}
 */
export function useAlertSound(options = {}) {
  const {
    volume = 1.0,
    src = DEFAULT_AUDIO_PATH,
    loop = false,
  } = options;

  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);

  useEffect(() => {
    const audio = new Audio(src);
    audio.preload = "auto";
    audio.volume = Math.max(0, Math.min(1, volume));
    audio.loop = loop;

    const onPlay = () => setIsPlaying(true);
    const onEnded = () => setIsPlaying(false);
    const onPause = () => setIsPlaying(false);

    audio.addEventListener("play", onPlay);
    audio.addEventListener("ended", onEnded);
    audio.addEventListener("pause", onPause);

    audioRef.current = audio;

    return () => {
      audio.removeEventListener("play", onPlay);
      audio.removeEventListener("ended", onEnded);
      audio.removeEventListener("pause", onPause);
      audio.pause();
      audio.currentTime = 0;
      audioRef.current = null;
    };
  }, [src, loop, volume]);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = Math.max(0, Math.min(1, volume));
    }
  }, [volume]);

  const play = useCallback(async () => {
    const audio = audioRef.current;
    if (!audio) return;

    try {
      audio.currentTime = 0;
      await audio.play();
    } catch (err) {
      if (err.name === "NotAllowedError") {
        console.warn(
          "Alert audio playback was prevented by browser autoplay policy. Audio will play once the user interacts with the page."
        );
      } else {
        console.error("Failed to play alert sound:", err);
      }
    }
  }, []);

  const stop = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;

    audio.pause();
    audio.currentTime = 0;
    setIsPlaying(false);
  }, []);

  return {
    play,
    stop,
    isPlaying,
  };
}

export default useAlertSound;
